import { useEffect, useState } from "react";

import { getAvailableAlertSubscriptions } from "@/api/endpoints/alert-subscriptions";
import BellIcon from "@/assets/icons/bell.svg";
import ChevronDownMicro from "@/assets/icons/chevron-down-micro.svg";
import UserMinus from "@/assets/icons/user-minus.svg";
import {
  SEARCH_SUBSCRIPTION_INTERVAL,
  SearchSubscriptionInterval,
} from "@/constants";
import { useSubscriptionHandler } from "@/data-hooks/useSubscriptions";
import { FEATURE } from "@/features";
import { useHasFeature } from "@/hooks/useHasFeature";
import { cx } from "@/utils";
import Button from "@components/Button";
import Clickable from "@components/Clickable";
import {
  Dropdown,
  DropdownCheckboxItem,
  DropdownHeading,
  DropdownItemIndicator,
  DropdownMenu,
  DropdownSeparator,
  DropdownToggle,
} from "@components/Dropdown";
import { CheckboxIndicator } from "@components/Field/Field/Checkbox";
import { i18n } from "@lingui/core";
import * as Collapsible from "@radix-ui/react-collapsible";
import { DropdownMenuCheckboxItemProps } from "@radix-ui/react-dropdown-menu";

import styles from "./QuickSubscribe.module.css";

export type QuickSubscribeProps = {
  className?: string;
  user_id: number;
  organization_uuid: string;
  search_uuid: string;
};

const labels = {
  [SEARCH_SUBSCRIPTION_INTERVAL.REALTIME]:
    L.SEARCH_SUBSCRIPTION_INTERVAL_REALTIME,
  [SEARCH_SUBSCRIPTION_INTERVAL.DAILY]: L.SEARCH_SUBSCRIPTION_INTERVAL_DAILY,
  [SEARCH_SUBSCRIPTION_INTERVAL.WEEKLY]: L.SEARCH_SUBSCRIPTION_INTERVAL_WEEKLY,
  [SEARCH_SUBSCRIPTION_INTERVAL.MONTHLY]:
    L.SEARCH_SUBSCRIPTION_INTERVAL_MONTHLY,
  [SEARCH_SUBSCRIPTION_INTERVAL.REALTIME_INSIGHTS_NEGATIVE]:
    L.SEARCH_SUBSCRIPTION_INTERVAL_REALTIME_INSIGHTS_NEG,
  [SEARCH_SUBSCRIPTION_INTERVAL.REALTIME_INSIGHTS_POSITIVE]:
    L.SEARCH_SUBSCRIPTION_INTERVAL_REALTIME_INSIGHTS_POS,
  [SEARCH_SUBSCRIPTION_INTERVAL.REALTIME_INSIGHTS_HIGH_REACH]:
    L.SEARCH_SUBSCRIPTION_INTERVAL_REALTIME_INSIGHTS_HIGH_REACH,
};

const Item = ({
  children,
  interval,
  ...rest
}: {
  children: React.ReactNode;
  interval: SearchSubscriptionInterval;
} & DropdownMenuCheckboxItemProps) => {
  return (
    <DropdownCheckboxItem
      key={SEARCH_SUBSCRIPTION_INTERVAL.REALTIME}
      onSelect={(e) => {
        // Don't close the menu when an item is clicked
        e.preventDefault();
      }}
      {...rest}
      data-testid={`quick-subscribe-${interval}`}
    >
      <DropdownItemIndicator forceMount>
        <CheckboxIndicator />
      </DropdownItemIndicator>
      {children}
    </DropdownCheckboxItem>
  );
};

const MetaItem = ({
  subscriptions,
  subscribe,
  unsubscribe,
  disabled,
}: {
  subscriptions: ReturnType<
    typeof useSubscriptionHandler
  >["subscriptionsByInterval"];
  unsubscribe: ReturnType<typeof useSubscriptionHandler>["unsubscribe"];
  subscribe: ReturnType<typeof useSubscriptionHandler>["subscribe"];
  disabled: boolean;
}) => {
  const hasHighReachMentionsFeature = useHasFeature(
    FEATURE.HAS_HIGH_REACH_MENTIONS_FEATURE
  );

  const positiveSub =
    subscriptions[SEARCH_SUBSCRIPTION_INTERVAL.REALTIME_INSIGHTS_POSITIVE];
  const negativeSub =
    subscriptions[SEARCH_SUBSCRIPTION_INTERVAL.REALTIME_INSIGHTS_NEGATIVE];
  const highReachSub =
    subscriptions[SEARCH_SUBSCRIPTION_INTERVAL.REALTIME_INSIGHTS_HIGH_REACH];

  const positiveSubscribed = positiveSub !== undefined;
  const negativeSubscribed = negativeSub !== undefined;
  const highReachSubscribed = highReachSub !== undefined;

  let allSubscribed = positiveSubscribed && negativeSubscribed;
  let anySubscribed = positiveSubscribed || negativeSubscribed;

  if (hasHighReachMentionsFeature) {
    allSubscribed = allSubscribed && highReachSubscribed;
    anySubscribed = anySubscribed || highReachSubscribed;
  }

  return (
    <Collapsible.Root>
      <DropdownCheckboxItem
        key={SEARCH_SUBSCRIPTION_INTERVAL.REALTIME}
        checked={anySubscribed}
        onSelect={(e) => {
          // Don't close the menu when an item is clicked
          e.preventDefault();
        }}
        data-testid="quick-subscribe-insights"
        onCheckedChange={() => {
          if (anySubscribed) {
            if (positiveSub) unsubscribe(positiveSub);
            if (negativeSub) unsubscribe(negativeSub);
            if (highReachSub) unsubscribe(highReachSub);
          } else {
            subscribe(SEARCH_SUBSCRIPTION_INTERVAL.REALTIME_INSIGHTS_NEGATIVE);
            subscribe(SEARCH_SUBSCRIPTION_INTERVAL.REALTIME_INSIGHTS_POSITIVE);
            if (hasHighReachMentionsFeature) {
              subscribe(
                SEARCH_SUBSCRIPTION_INTERVAL.REALTIME_INSIGHTS_HIGH_REACH
              );
            }
          }
        }}
      >
        <DropdownItemIndicator forceMount>
          <CheckboxIndicator indecisive={!allSubscribed} />
        </DropdownItemIndicator>
        {L.SEARCH_SUBSCRIPTION_IMPORTANT_HEADER}
        <Collapsible.Trigger asChild>
          <Clickable onClick={(e) => e.stopPropagation()}>
            <ChevronDownMicro className={styles.chevron} />
          </Clickable>
        </Collapsible.Trigger>
      </DropdownCheckboxItem>

      <Collapsible.Content>
        <Item
          className={styles.sub}
          interval={SEARCH_SUBSCRIPTION_INTERVAL.REALTIME_INSIGHTS_POSITIVE}
          disabled={disabled}
          checked={positiveSubscribed}
          onCheckedChange={() => {
            if (positiveSub) unsubscribe(positiveSub);
            else
              subscribe(
                SEARCH_SUBSCRIPTION_INTERVAL.REALTIME_INSIGHTS_POSITIVE
              );
          }}
        >
          {i18n._(
            labels[SEARCH_SUBSCRIPTION_INTERVAL.REALTIME_INSIGHTS_POSITIVE]
          )}
        </Item>

        <Item
          className={styles.sub}
          interval={SEARCH_SUBSCRIPTION_INTERVAL.REALTIME_INSIGHTS_NEGATIVE}
          disabled={disabled}
          checked={negativeSubscribed}
          onCheckedChange={() => {
            if (negativeSub) unsubscribe(negativeSub);
            else
              subscribe(
                SEARCH_SUBSCRIPTION_INTERVAL.REALTIME_INSIGHTS_NEGATIVE
              );
          }}
        >
          {i18n._(
            labels[SEARCH_SUBSCRIPTION_INTERVAL.REALTIME_INSIGHTS_NEGATIVE]
          )}
        </Item>

        {hasHighReachMentionsFeature && (
          <Item
            className={styles.sub}
            interval={SEARCH_SUBSCRIPTION_INTERVAL.REALTIME_INSIGHTS_HIGH_REACH}
            disabled={disabled}
            checked={highReachSubscribed}
            onCheckedChange={() => {
              if (highReachSub) unsubscribe(highReachSub);
              else
                subscribe(
                  SEARCH_SUBSCRIPTION_INTERVAL.REALTIME_INSIGHTS_HIGH_REACH
                );
            }}
          >
            {i18n._(
              labels[SEARCH_SUBSCRIPTION_INTERVAL.REALTIME_INSIGHTS_HIGH_REACH]
            )}
          </Item>
        )}
      </Collapsible.Content>
    </Collapsible.Root>
  );
};

const QuickSubscribe: React.FunctionComponent<QuickSubscribeProps> = ({
  className,
  user_id,
  organization_uuid,
  search_uuid,
}) => {
  const [choices, setChoices] = useState<SearchSubscriptionInterval[]>([]);

  useEffect(() => {
    getAvailableAlertSubscriptions({
      user_id,
      organization_uuid,
    }).then((res) => {
      if (res.ok && res.data) {
        setChoices(res.data);
      }
    });
  }, [organization_uuid, user_id]);

  const {
    subscriptions,
    unsubscribe,
    unsubscribeAll,
    isSaving,
    isLoading,
    subscriptionsByInterval,
    subscribe,
  } = useSubscriptionHandler({
    organization_uuid,
    user_id,
    search_uuid,
  });

  const isSubscribed = subscriptions && subscriptions?.length > 0;

  return (
    <Dropdown>
      <DropdownToggle asChild disabled={isLoading}>
        <Button
          data-testid="quick-subscribe"
          disabled={isLoading}
          className={cx(styles.root, className)}
          icon={isSubscribed ? BellIcon : ChevronDownMicro}
          iconPlacement={isSubscribed ? "before" : "after"}
          variant="secondary"
        >
          {isSubscribed
            ? L.REPORTS_QUICK_SUBSCRIBING
            : L.REPORTS_QUICK_SUBSCRIBE}
        </Button>
      </DropdownToggle>

      <DropdownMenu>
        <DropdownHeading>{L.REPORTS_QUICK_TITLE}</DropdownHeading>
        {/** Due to the nature of these being passed down */}

        {choices.includes(SEARCH_SUBSCRIPTION_INTERVAL.REALTIME) && (
          <Item
            interval={SEARCH_SUBSCRIPTION_INTERVAL.REALTIME}
            disabled={isSaving}
            checked={
              subscriptionsByInterval[SEARCH_SUBSCRIPTION_INTERVAL.REALTIME] !==
              undefined
            }
            onCheckedChange={() => {
              const sub =
                subscriptionsByInterval[SEARCH_SUBSCRIPTION_INTERVAL.REALTIME];
              if (sub) unsubscribe(sub);
              else subscribe(SEARCH_SUBSCRIPTION_INTERVAL.REALTIME);
            }}
          >
            {i18n._(labels[SEARCH_SUBSCRIPTION_INTERVAL.REALTIME])}
          </Item>
        )}

        {choices.includes(
          SEARCH_SUBSCRIPTION_INTERVAL.REALTIME_INSIGHTS_NEGATIVE
        ) &&
          choices.includes(
            SEARCH_SUBSCRIPTION_INTERVAL.REALTIME_INSIGHTS_POSITIVE
          ) && (
            <MetaItem
              subscribe={subscribe}
              unsubscribe={unsubscribe}
              disabled={isSaving}
              subscriptions={subscriptionsByInterval}
            />
          )}

        {choices.includes(SEARCH_SUBSCRIPTION_INTERVAL.DAILY) && (
          <Item
            interval={SEARCH_SUBSCRIPTION_INTERVAL.DAILY}
            disabled={isSaving}
            checked={
              subscriptionsByInterval[SEARCH_SUBSCRIPTION_INTERVAL.DAILY] !==
              undefined
            }
            onCheckedChange={() => {
              const sub =
                subscriptionsByInterval[SEARCH_SUBSCRIPTION_INTERVAL.DAILY];
              if (sub) unsubscribe(sub);
              else subscribe(SEARCH_SUBSCRIPTION_INTERVAL.DAILY);
            }}
          >
            {i18n._(labels[SEARCH_SUBSCRIPTION_INTERVAL.DAILY])}
          </Item>
        )}

        {choices.includes(SEARCH_SUBSCRIPTION_INTERVAL.WEEKLY) && (
          <Item
            interval={SEARCH_SUBSCRIPTION_INTERVAL.WEEKLY}
            disabled={isSaving}
            checked={
              subscriptionsByInterval[SEARCH_SUBSCRIPTION_INTERVAL.WEEKLY] !==
              undefined
            }
            onCheckedChange={() => {
              const sub =
                subscriptionsByInterval[SEARCH_SUBSCRIPTION_INTERVAL.WEEKLY];
              if (sub) unsubscribe(sub);
              else subscribe(SEARCH_SUBSCRIPTION_INTERVAL.WEEKLY);
            }}
          >
            {i18n._(labels[SEARCH_SUBSCRIPTION_INTERVAL.WEEKLY])}
          </Item>
        )}
        <DropdownSeparator />

        <DropdownHeading>{L.REPORTS_QUICK_TITLE_MONTHLY}</DropdownHeading>
        {choices.includes(SEARCH_SUBSCRIPTION_INTERVAL.MONTHLY) && (
          <Item
            interval={SEARCH_SUBSCRIPTION_INTERVAL.MONTHLY}
            disabled={isSaving}
            checked={
              subscriptionsByInterval[SEARCH_SUBSCRIPTION_INTERVAL.MONTHLY] !==
              undefined
            }
            onCheckedChange={() => {
              const sub =
                subscriptionsByInterval[SEARCH_SUBSCRIPTION_INTERVAL.MONTHLY];
              if (sub) unsubscribe(sub);
              else subscribe(SEARCH_SUBSCRIPTION_INTERVAL.MONTHLY);
            }}
          >
            {i18n._(labels[SEARCH_SUBSCRIPTION_INTERVAL.MONTHLY])}
          </Item>
        )}
        <DropdownSeparator />
        <DropdownCheckboxItem
          onSelect={() => unsubscribeAll()}
          data-testid="quick-subscribe-none"
          disabled={!isSubscribed || isSaving}
        >
          <UserMinus />
          {L.SEARCH_SUBSCRIPTION_END}
        </DropdownCheckboxItem>
      </DropdownMenu>
    </Dropdown>
  );
};

export default QuickSubscribe;
